import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useRef,
  ReactNode,
} from 'react';
import { useAuth } from '../context/AuthContext';
import useWebSocket, { ReadyState } from 'react-use-websocket';

interface WebSocketContextType {
  messages: string[];
  isConnected: boolean;
  hasConnection: boolean;
  readyState: ReadyState;
}

const defaultWebSocketContextValue: WebSocketContextType = {
  messages: [],
  isConnected: false,
  hasConnection: false,
  readyState: ReadyState.CLOSED,
};

const WebSocketContext = createContext<WebSocketContextType>(
  defaultWebSocketContextValue
);

export const useSocketContext = () => useContext(WebSocketContext);

interface WebSocketProviderProps {
  children: ReactNode;
}

const WS_URL = process.env.REACT_APP_WS_URL;

const getOnLineStatus = () =>
  typeof navigator !== 'undefined' && typeof navigator.onLine === 'boolean'
    ? navigator.onLine
    : true;

const useNavigatorOnLine = () => {
  const [status, setStatus] = React.useState(getOnLineStatus());

  const setOnline = () => setStatus(true);
  const setOffline = () => setStatus(false);

  React.useEffect(() => {
    window.addEventListener('online', setOnline);
    window.addEventListener('offline', setOffline);

    return () => {
      window.removeEventListener('online', setOnline);
      window.removeEventListener('offline', setOffline);
    };
  }, []);

  return status;
};

export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({
  children,
}) => {
  const [messages, setMessages] = useState<string[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const heartbeatInterval = useRef<NodeJS.Timeout | number>();
  const auth = useAuth();
  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const hasConnection = useNavigatorOnLine();

  const hasConnectionRef = useRef(hasConnection);

  function closeWebSocket() {
    setSocketUrl(null);
  }

  function connectWebSocket() {
    if (auth.token === null) {
      return;
    }
    setSocketUrl(`${WS_URL}/UserMessages?token=${auth.token}`);
  }

  React.useEffect(() => {
    console.log(hasConnection ? 'Online' : 'Offline');
    hasConnectionRef.current = hasConnection;
  }, [hasConnection]);

  const { sendMessage, readyState } = useWebSocket(socketUrl, {
    onMessage: (event: any) => {
      setMessages([event.data]);
    },
    onError: (error: any) => {},
    onClose: (event: any) => {
      if (event.reason === 'INVALID_USER') auth.setToken(null);
    },
    share: false,
    retryOnError: true,
    shouldReconnect: () => true,
    protocols: ['spottEvents'],
    reconnectInterval: 5000,
    reconnectAttempts: 20000,
  });

  useEffect(() => {
    console.log('readyState', readyState);

    if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    if (readyState === ReadyState.OPEN) {
      setIsConnected(true);
      var interval = setInterval(() => {
        if (hasConnectionRef.current) {
          console.log('Sending heartbeat');
          sendMessage(JSON.stringify({ type: 'heartbeat' }));
        }
      }, 30000); // 30 segundos
      heartbeatInterval.current = interval;
    } else {
      setIsConnected(false);
    }
  }, [readyState]);

  useEffect(() => {
    return () => {
      if (heartbeatInterval.current) clearInterval(heartbeatInterval.current);
    };
  }, []);

  useEffect(() => {
    closeWebSocket();
    if (auth.token) {
      connectWebSocket();
    } else {
      closeWebSocket();
    }

    return () => {};
  }, [auth?.token]);

  return (
    <WebSocketContext.Provider
      value={{ messages, isConnected, hasConnection, readyState }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};
